import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import {
    getBlogPost, createBlogPost, updateBlogPost,
    getBlogCategories, createBlogCategory
} from '../../services/BlogService';
import logger from '../../utils/logger';

function BlogPost() {
    const { id } = useParams();
    const history = useHistory();
    const { formatMessage } = useIntl();
    const t = (msgId) => formatMessage({ id: msgId });

    const isNew = !id || id === 'new';
    const [categories, setCategories] = useState([]);
    const [notification, setNotification] = useState({});
    const [newCatName, setNewCatName] = useState('');

    const [post, setPost] = useState({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        coverImage: '',
        categoryId: '',
        authorName: '',
        tags: '',
        isPublished: false,
        isFeatured: false,
        readTimeMinutes: 5,
        metaTitle: '',
        metaDescription: '',
    });

    useEffect(() => {
        getBlogCategories()
            .then(setCategories)
            .catch(err => logger.error(err));

        if (!isNew) {
            getBlogPost(id)
                .then(data => setPost({
                    ...data,
                    tags: Array.isArray(data.tags) ? data.tags.join(', ') : (data.tags || ''),
                    categoryId: data.categoryId || '',
                    publishedAt: data.publishedAt ? data.publishedAt.slice(0, 16) : '',
                }))
                .catch(err => setNotification({ type: 'error', text: err.response?.data || err.message }));
        }
    }, [id, isNew]);

    function onChange(e) {
        const { id: fieldId, value, type, checked } = e.target;
        setPost(prev => ({ ...prev, [fieldId]: type === 'checkbox' ? checked : value }));
    }

    function generateSlug(title) {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
    }

    function onTitleChange(e) {
        const title = e.target.value;
        setPost(prev => ({
            ...prev,
            title,
            slug: isNew ? generateSlug(title) : prev.slug,
        }));
    }

    async function onAddCategory() {
        if (!newCatName.trim()) return;
        try {
            const slug = generateSlug(newCatName);
            const cat = await createBlogCategory({ name: newCatName.trim(), slug });
            setCategories(prev => [...prev, cat]);
            setNewCatName('');
            setNotification({ type: 'success', text: 'Category created' });
        } catch (err) {
            setNotification({ type: 'error', text: err.response?.data || err.message });
        }
    }

    async function onSave() {
        const payload = {
            ...post,
            tags: post.tags ? post.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
            categoryId: post.categoryId ? parseInt(post.categoryId) : null,
            readTimeMinutes: parseInt(post.readTimeMinutes) || 5,
            publishedAt: post.isPublished && !post.publishedAt ? new Date().toISOString() : post.publishedAt || null,
        };

        try {
            if (isNew) {
                const created = await createBlogPost(payload);
                setNotification({ type: 'success', text: 'Post created' });
                history.replace(`/blog/${created.id}`);
            } else {
                await updateBlogPost(id, payload);
                setNotification({ type: 'success', text: 'Post saved' });
            }
        } catch (err) {
            setNotification({ type: 'error', text: err.response?.data || err.message });
        }
    }

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div>
                        <h1 className="h4">{isNew ? t('blog.newPost') : t('blog.editPost')}</h1>
                    </div>
                    <div className="btn-toolbar mb-md-0 gap-2">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => history.push('/blog')}>
                            ← {t('blog.backToPosts')}
                        </button>
                        <button className="btn btn-sm btn-gray-800" onClick={onSave}>
                            {t('blog.save')}
                        </button>
                    </div>
                </div>

                {/* gx-0: Bootstrap's .row negative gutter (-12px) is wider than the
                    phone padding .content gets (.6rem), which pushed the page 2px
                    sideways at 390px. Column spacing comes from pe-lg-4 instead. */}
                <div className="row gx-0">
                    <div className="col-lg-8 pe-lg-4">
                        <div className="card card-body border-0 shadow mb-4">
                            <div className="mb-3">
                                <label className="form-label">Title *</label>
                                <input id="title" className="form-control" value={post.title} onChange={onTitleChange} />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Slug *</label>
                                <input id="slug" className="form-control font-monospace" value={post.slug} onChange={onChange} />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Excerpt</label>
                                <textarea id="excerpt" className="form-control" rows={2} value={post.excerpt || ''} onChange={onChange} />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Content (HTML)</label>
                                <textarea id="content" className="form-control font-monospace" rows={16} value={post.content || ''} onChange={onChange} />
                            </div>
                        </div>

                        <div className="card card-body border-0 shadow mb-4">
                            <h5 className="mb-3">SEO</h5>
                            <div className="mb-3">
                                <label className="form-label">Meta Title</label>
                                <input id="metaTitle" className="form-control" value={post.metaTitle || ''} onChange={onChange} />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Meta Description</label>
                                <textarea id="metaDescription" className="form-control" rows={2} value={post.metaDescription || ''} onChange={onChange} />
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-4">
                        <div className="card card-body border-0 shadow mb-4">
                            <h5 className="mb-3">Publish</h5>
                            <div className="form-check form-switch mb-3">
                                <input type="checkbox" id="isPublished" className="form-check-input" checked={post.isPublished || false} onChange={onChange} />
                                <label className="form-check-label" htmlFor="isPublished">Published</label>
                            </div>
                            <div className="form-check form-switch mb-3">
                                <input type="checkbox" id="isFeatured" className="form-check-input" checked={post.isFeatured || false} onChange={onChange} />
                                <label className="form-check-label" htmlFor="isFeatured">Featured</label>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Published At</label>
                                <input id="publishedAt" type="datetime-local" className="form-control" value={post.publishedAt || ''} onChange={onChange} />
                            </div>
                            <button className="btn btn-gray-800 w-100" onClick={onSave}>{t('blog.save')}</button>
                        </div>

                        <div className="card card-body border-0 shadow mb-4">
                            <h5 className="mb-3">Details</h5>
                            <div className="mb-3">
                                <label className="form-label">Category</label>
                                <select id="categoryId" className="form-select" value={post.categoryId || ''} onChange={onChange}>
                                    <option value="">— None —</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                                <div className="input-group mt-2">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="New category name"
                                        value={newCatName}
                                        onChange={e => setNewCatName(e.target.value)}
                                    />
                                    <button className="btn btn-sm btn-outline-secondary" onClick={onAddCategory}>+</button>
                                </div>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Author</label>
                                <input id="authorName" className="form-control" value={post.authorName || ''} onChange={onChange} />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Tags (comma-separated)</label>
                                <input id="tags" className="form-control" placeholder="crypto, trading, bitcoin" value={post.tags || ''} onChange={onChange} />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Read Time (min)</label>
                                <input id="readTimeMinutes" type="number" min="1" className="form-control" value={post.readTimeMinutes || 5} onChange={onChange} />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Cover Image URL</label>
                                <input id="coverImage" className="form-control" placeholder="https://..." value={post.coverImage || ''} onChange={onChange} />
                                {post.coverImage && (
                                    <img src={post.coverImage} alt="cover preview" className="mt-2 rounded" style={{ width: '100%', height: 100, objectFit: 'cover' }} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    );
}

export default BlogPost;
